import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Badge, Spinner, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataManager } from '../../../../../core/data-manager';
import { ResourceManager } from '../../../../../core/resource-manager';
import './style.css';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
const VIEWABLE_EXTENSIONS = ['pdf', ...IMAGE_EXTENSIONS];

function isImage(ext) {
    return IMAGE_EXTENSIONS.includes((ext || '').toLowerCase());
}

function isViewable(ext) {
    return VIEWABLE_EXTENSIONS.includes((ext || '').toLowerCase());
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
}

function getExtensionColor(ext) {
    const colors = {
        'pdf': '#dc3545', 'doc': '#2b579a', 'docx': '#2b579a',
        'xls': '#217346', 'xlsx': '#217346', 'csv': '#217346',
        'png': '#0d6efd', 'jpg': '#0d6efd', 'jpeg': '#0d6efd', 'gif': '#0d6efd',
        'webp': '#0d6efd', 'svg': '#0d6efd', 'zip': '#f0ad4e', 'rar': '#f0ad4e',
        'mp4': '#6f42c1', 'mp3': '#fd7e14',
    };
    return colors[(ext || '').toLowerCase()] || '#6c757d';
}

async function downloadFile(rD) {
    var response = await ResourceManager.getFile(rD.hash, rD.entity);
    if (response.success === 1 && response.base64) {
        var raw = response.base64;
        var byteString, mime;
        if (raw.indexOf(',') !== -1) {
            var parts = raw.split(',');
            var mimeMatch = parts[0].match(/:(.*?);/);
            mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
            byteString = atob(parts[1]);
        } else {
            mime = 'application/octet-stream';
            byteString = atob(raw);
        }
        var ab = new ArrayBuffer(byteString.length);
        var ia = new Uint8Array(ab);
        for (var i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        var blob = new Blob([ab], { type: mime });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = rD.filename || (rD.hash + '.' + rD.extension);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
}

export default function ResourceDetail({ resource, onClose, getFileUrl }) {
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);
    const [downloadingFile, setDownloadingFile] = useState(false);

    useEffect(() => {
        if (resource?.idUser) {
            loadUser(resource.idUser);
        }
    }, [resource]);

    const loadUser = async (userId) => {
        setLoadingUser(true);
        try {
            const response = await DataManager.getItem('user', userId);
            if (response.success === 1 && response.body) {
                const userData = Array.isArray(response.body) ? response.body[0] : response.body;
                if (userData) setUser(userData);
            }
        } catch (error) {
            console.error('Error loading user:', error);
        } finally {
            setLoadingUser(false);
        }
    };

    if (!resource) return null;

    const fileUrl = getFileUrl(resource);
    const ext = (resource.extension || '').toLowerCase();

    const renderPreview = () => {
        if (isImage(ext)) {
            return (
                <div className="resource-detail-preview">
                    <img src={fileUrl} alt={resource.filename} className="resource-detail-image" />
                </div>
            );
        }
        if (ext === 'pdf') {
            return (
                <div className="resource-detail-preview resource-detail-pdf">
                    <iframe src={fileUrl+'#toolbar=0'} title={resource.filename} className="resource-detail-iframe" />
                </div>
            );
        }
        return (
            <div className="resource-detail-preview resource-detail-no-preview">
                <FontAwesomeIcon icon="file" size="4x" className="text-muted mb-2" />
                <p className="text-muted mb-0">Anteprima non disponibile</p>
                <small className="text-muted">Apri il file per visualizzarlo</small>
            </div>
        );
    };

    const getUserDisplay = () => {
        if (loadingUser) return <Spinner animation="border" size="sm" />;
        if (!user) return <span className="text-muted">ID: {resource.idUser}</span>;
        return user.username || `User #${resource.idUser}`;
    };

    return (
        <Modal scrollable={true} show={true} onHide={onClose} size="lg" centered className="resource-detail-modal">
            <Modal.Header closeButton>
                <Modal.Title className="resource-detail-title">
                    <FontAwesomeIcon icon="file" className="me-2" style={{ color: getExtensionColor(ext) }} />
                    {resource.filename}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {renderPreview()}

                <div className="resource-detail-info mt-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="tag" className="me-1" /> Nome file</label>
                                <span>{resource.filename}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="puzzle-piece" className="me-1" /> Estensione</label>
                                <span>
                                    <Badge
                                        bg="light" text="dark"
                                        style={{ fontSize: '0.85rem', border: '1px solid #dee2e6' }}
                                    >
                                        .{resource.extension}
                                    </Badge>
                                </span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="database" className="me-1" /> Entit&agrave;</label>
                                <span>{resource.entity || <span className="text-muted">-</span>}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="user" className="me-1" /> Caricato da</label>
                                <span>{getUserDisplay()}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="calendar" className="me-1" /> Data caricamento</label>
                                <span>{formatDate(resource.date)}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="clock" className="me-1" /> Ultimo aggiornamento</label>
                                <span>{formatDate(resource.lastUpdate)}</span>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="fingerprint" className="me-1" /> Hash</label>
                                <span className="resource-detail-hash">{resource.hash}</span>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="resource-detail-field">
                                <label><FontAwesomeIcon icon="link" className="me-1" /> URL</label>
                                <span className="resource-detail-url">
                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">{fileUrl}</a>
                                </span>
                            </div>
                        </Col>
                    </Row>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="success"
                    disabled={downloadingFile}
                    onClick={async () => {
                        setDownloadingFile(true);
                        try {
                            await downloadFile(resource);
                        } finally {
                            setDownloadingFile(false);
                        }
                    }}
                >
                    <FontAwesomeIcon icon="download" className="me-1" />
                    {downloadingFile ? 'Download...' : 'Scarica file'}
                </Button>
                {isViewable(ext) && (
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                    >
                        <FontAwesomeIcon icon="external-link-alt" className="me-1" />
                        Apri file
                    </a>
                )}
                <Button variant="secondary" onClick={onClose}>
                    Chiudi
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
